import { useApolloClient, useLazyQuery, useMutation } from "@apollo/client"
import { MenuItem } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomFormGroup from "components/Atoms/CustomFormGroup"
import CustomSpinner from "components/Atoms/CustomSpinner"
import StringInput from "components/Atoms/StringInput"
import Error from "components/Common/Error"
import Authorization from "components/Molecules/Authorization"
import DynamicMultiSelector from "components/Molecules/DynamicMultiSelector"
import DynamicSelector from "components/Molecules/DynamicSelector"
import FileUpload from "components/Molecules/FileUpload"
import FormController from "components/Molecules/FormController"
import FormLayout from "components/Molecules/FormLayout"
import MutationOptions from "components/Organisms/MutationOptions"
import UserSuggest from "components/Organisms/UserSuggest"
import FileUploadDialog from "components/Templates/FileUploadDialog"
import GoogleMapDialog from "components/Templates/GoogleMapDialog"
import { DELETE_MEDIA } from "graphql/media/media.mutations.gql"
import { BANKS, COMPANIES, KYCS } from "graphql/selector.queries.gql"
import { VEHICLE_LIST_FRAG } from "graphql/vehicle/vehicle.fragments.gql"
import { ADD_UPDATE_VEHICLE_OWNER } from "graphql/vehicle/vehicle.mutations.gql"
import { VEHICLE_OWNER_DETAILS } from "graphql/vehicle/vehicle.queries.gql"
import useErrors from "hooks/useErrors"
import useIdParam from "hooks/useIdParam"
import { cloneDeep, get, pick, set } from "lodash-es"
import vehicleOwnerModel from "models/organisms/vehicle.owner.model"
import React, { useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import AppToaster, { ErrorToast } from "utils/AppToaster"
import { getId } from "utils/custom.utils"
import {
  DocumentTypes,
  isInValidDocumentPresent,
  isValidDocument,
} from "utils/documentHelper"
import { remove } from "utils/ds.utils"
import { removeTypename } from "utils/objectUtils"

const VehicleOwnerRegistration = () => {
  const location = useLocation()
  const state = location.state as any
  const navigate = useNavigate()
  const id = useIdParam()
  const [isFileUploadOpen, setUpload] = useState(false)

  const [ownerDetails, changeOwnerDetails] = useState<any>({
    kycDetails: [{}],
    bankDetails: [{}],
  })
  const [errors] = useErrors({
    schema: vehicleOwnerModel,
    state: ownerDetails,
  })
  const client = useApolloClient()

  const [deleteMedia] = useMutation(DELETE_MEDIA)

  function handleMediaDelete(id) {
    deleteMediaMutation(id)
  }

  function deleteMediaMutation(id) {
    deleteMedia({
      variables: {
        ids: [id],
      },
    })
  }

  // The data that is sent to the server via Mutation
  // const parsedVariables = useMemo(() => {
  //   return { ...omit(ownerDetails, ["customerId"]), vehicleId: state.id };
  // }, [ownerDetails, state]);
  const vehicleId = state?.id
  const parsedVariables = useMemo(() => {
    const bank = ownerDetails?.bankDetails?.[0]
    return {
      ...pick(ownerDetails, [
        "id",
        "firstName",
        "lastName",
        "number",
        "email",
        "address",
      ]),
      vehicleId,
      companies: ownerDetails?.companies?.map(getId),
      kycDetails: ownerDetails?.kycDetails
        ?.filter(k => k.kycType)
        .map(k => ({
          ...k,
          kycType: k?.kycType?.id,
        })),
      bankDetail: {
        ...bank,
        bank: bank?.bank?.id,
        attachments: bank?.attachments?.map(p => ({
          id: p?.id,
          url: p?.url,
          mediaType: p?.mediaType ?? "PHT",
        })),
      },
    }
  }, [ownerDetails, vehicleId])
  const [getUserDetail, { loading, error }] = useLazyQuery(
    VEHICLE_OWNER_DETAILS,
    {
      onCompleted({ userDetails: ud }) {
        const bank = ud.bankDetails
        const kyc = ud.kycDetails
        changeOwnerDetails(
          removeTypename({
            ...ud,
            bankDetails: bank.length ? bank : [{}],
            kycDetails: kyc.length ? kyc : [{}],
          })
        )
      },
    }
  )
  useEffect(() => {
    if (id && !ownerDetails.id) {
      getUserDetail({ variables: { id } })
    }
  }, [id, ownerDetails.id, getUserDetail])
  if (error) return <Error />
  if (loading) return <CustomSpinner />

  const type = state?.vehicleType

  const showToast = data => {
    const owner = get(data, "addUpdateVehicleOwner.owner")
    AppToaster.show({
      message: `${owner.firstName} ${owner.lastName} assigned to vehicle ${state.registrationNumber}`,
      intent: "success",
    })
  }
  const showError = error => {
    AppToaster.show({
      message: error.message,
      intent: "danger",
    })
  }
  const handleChange = (path, value) => {
    if (ownerDetails.id) {
      if (path === "firstName" || path === "lastName" || path === "number") {
        AppToaster.show(
          {
            message: <span>Unable to change basic details of the user.</span>,
            timeout: 10000,
            intent: "warning",
            icon: "warning-sign",
          },
          "unauthorized"
        )
        return
      }
    }
    const ownerDetailsCopy = cloneDeep(ownerDetails)
    set(ownerDetailsCopy, path, value)
    changeOwnerDetails(ownerDetailsCopy)
  }

  return (
    <FormLayout type="Owner" id={id}>
      <Authorization required={["vt_add_vehicle", "vt_update_vehicle"]} any>
        {({ disabled }) => {
          return (
            <>
              <div className="flex items-baseline">
                <h5 className="mr-6 text-2xl">
                  <span className="text-muted">Vehicle - </span>{" "}
                  {state.registrationNumber}{" "}
                  <span className="text-muted">
                    ({`${type.name} - ${type.category}`})
                  </span>
                </h5>
              </div>
              <hr className="mt-0" />
              <UserSuggest
                label={
                  <span>
                    Populate <span className="vt-warning">this owner</span> from
                    the list of existing users
                  </span>
                }
                className="mb-5"
                disabled={disabled}
                onChange={user => {
                  if (user?.id) {
                    getUserDetail({
                      variables: { id: user?.id },
                    })
                  } else {
                    changeOwnerDetails({})
                  }
                }}
              />
              <FormController
                disableAll={true}
                disabled={disabled}
                source={ownerDetails}
                errors={errors.details}
                onChange={handleChange}
              >
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <StringInput
                      label="First Name"
                      path="firstName"
                      required
                    ></StringInput>
                  </div>
                  <div className="col-span-3">
                    <StringInput
                      label="Last Name"
                      path="lastName"
                      required
                    ></StringInput>
                  </div>
                  <div className="col-span-3">
                    <StringInput
                      label="Phone Number"
                      helperText="eg +919999999999"
                      path="number"
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-12 gap-4">
                  <div className="col-span-3">
                    <CustomFormGroup big label="Address" required>
                      <GoogleMapDialog
                        disabled={disabled}
                        btnProps={{
                          big: true,
                          fill: true,
                          intent: "none",
                          text: "Select Address",
                        }}
                        path="address"
                      />
                    </CustomFormGroup>
                  </div>

                  <div className="col-span-3">
                    <StringInput
                      label="Transporter Code"
                      path="customerId"
                      disabled={true}
                    />
                  </div>
                  <div className="col-span-6">
                    <DynamicMultiSelector
                      query={COMPANIES}
                      label="Companies"
                      required
                      path="companies"
                      big
                      fill
                      disabled={false}
                    />
                  </div>
                </div>
                <h6 className="mt-6 vt-section-header">
                  KYC Details{" "}
                  <CustomButton
                    icon="plus"
                    intent="success"
                    minimal
                    onClick={e => {
                      changeOwnerDetails(prev => ({
                        ...prev,
                        kycDetails: prev?.kycDetails?.concat({}),
                      }))
                    }}
                  />
                </h6>
                <hr className="mt-0" />
                {ownerDetails?.kycDetails?.map((d, idx) => {
                  const pan = ownerDetails?.kycDetails?.find(doc => {
                    return doc?.kycType?.name === DocumentTypes.PAN
                  })?.idNumber

                  const base = `kycDetails.${idx}.`
                  return (
                    <div key={d?.id ?? idx} className="grid grid-cols-12 gap-4">
                      <div className="col-span-3">
                        <DynamicSelector
                          big
                          required
                          query={KYCS}
                          path={base + "kycType"}
                          label="Type"
                          helperText="eg. Aadhar"
                          itemRenderer={(item, { handleClick, modifiers }) => {
                            return (
                              <MenuItem
                                disabled={
                                  ownerDetails?.kycDetails?.find(
                                    k => k?.kycType?.id === item?.id
                                  )
                                    ? true
                                    : false
                                }
                                active={modifiers.active}
                                key={item.id}
                                onClick={handleClick}
                                text={item?.name}
                              />
                            )
                          }}
                        />
                      </div>
                      <div className="col-span-3">
                        <StringInput
                          path={base + "idNumber"}
                          label="Number"
                          helperText="eg. 435678986545"
                          error={
                            isValidDocument({
                              documentKind: d?.kycType?.name,
                              documentNumber: d?.idNumber,
                              pan: pan,
                              state: ownerDetails?.address?.state,
                            }) || ""
                          }
                        />
                      </div>
                      <div className="col-span-3">
                        <FileUpload
                          big={true}
                          path={base + "photo"}
                          label="Kyc Photo"
                        />
                      </div>
                      <div className="col-span-1 flex justify-center items-center">
                        <CustomButton
                          className="mb-4"
                          icon="trash"
                          intent="danger"
                          minimal
                          onClick={e => {
                            changeOwnerDetails(prev => ({
                              ...prev,
                              kycDetails: remove(prev?.kycDetails, idx),
                            }))
                          }}
                        />
                      </div>
                    </div>
                  )
                })}
                <h6 className="mt-6 vt-section-header">Bank Details </h6>
                <hr className="mt-0" />
                {ownerDetails?.bankDetails?.map((b, idx) => {
                  const base = `bankDetails.${idx}.`
                  return (
                    <React.Fragment key={b?.id ?? idx}>
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                          <StringInput
                            path={base + "accountHolderName"}
                            label="Account Holder"
                            helperText="account holder name"
                          />
                        </div>
                        <div className="col-span-3">
                          <DynamicSelector
                            big
                            query={BANKS}
                            label="Bank"
                            helperText="eg. ICICI"
                            path={base + "bank"}
                          />
                        </div>
                        <div className="col-span-3">
                          <StringInput
                            path={base + "accountNumber"}
                            label="Account Number"
                            helperText="eg. 00006746464442352"
                          />
                        </div>
                        <div className="col-span-3">
                          <StringInput
                            path={base + "branchName"}
                            label="Branch"
                            helperText="Thatte Nagar"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-3">
                          <StringInput
                            path={base + "ifscCode"}
                            label="IFSC Number"
                            helperText="eg. ICIC0006517"
                          />
                        </div>
                        <div className="col-span-3">
                          <CustomFormGroup
                            label="Attachments"
                            labelInfo={
                              <span className="vt-highlight vt-dark">
                                ({b?.attachments?.length || "No"} Photos
                                uploaded)
                              </span>
                            }
                          >
                            <CustomButton
                              intent="primary"
                              fill
                              icon="cloud-upload"
                              text="Upload Photos"
                              onClick={() => setUpload(true)}
                            />
                          </CustomFormGroup>
                          <FileUploadDialog
                            disabled={disabled}
                            onDelete={e => {
                              handleMediaDelete(e?.id)
                              const ownerDetailsCopy = cloneDeep(ownerDetails)
                              const attachments =
                                ownerDetailsCopy?.bankDetails?.[
                                  idx
                                ]?.attachments?.filter(
                                  att => att?.id !== e?.id
                                ) || []
                              set(
                                ownerDetailsCopy,
                                base + ".attachments",
                                attachments
                              )
                              changeOwnerDetails(ownerDetailsCopy)
                            }}
                            path={base + "attachments"}
                            isOpen={isFileUploadOpen}
                            onClose={() => setUpload(false)}
                          />
                        </div>
                      </div>
                    </React.Fragment>
                  )
                })}
              </FormController>
              <div className="flex justify-end">
                <MutationOptions
                  // disabled={false}
                  mutation={ADD_UPDATE_VEHICLE_OWNER}
                  variables={{ owner: parsedVariables }}
                  onError={showError}
                  validate={() => {
                    const docIsValid = isInValidDocumentPresent({
                      documents: ownerDetails?.kycDetails,
                      state: ownerDetails?.address?.state,
                    })

                    if (docIsValid !== undefined && !docIsValid) {
                      ErrorToast("Please resolve all errors before proceeding")
                      return false
                    }

                    if (errors.latest) {
                      AppToaster.show({
                        message: errors.latest,
                        intent: "danger",
                        icon: "cross",
                      })
                      return false
                    }
                    return true
                  }}
                  update={(cache, result) => {
                    const owner = get(
                      result,
                      "data.addUpdateVehicleOwner.owner"
                    )

                    const vehicle = client.readFragment({
                      id: `VehicleType:${state.id}`,
                      fragment: VEHICLE_LIST_FRAG,
                    })

                    client.writeFragment({
                      id: `VehicleType:${state.id}`,
                      fragment: VEHICLE_LIST_FRAG,
                      data: {
                        ...vehicle,
                        owner: pick(owner, [
                          "id",
                          "fullName",
                          "number",
                          "__typename",
                        ]),
                      },
                    })
                  }}
                  onSave={data => {
                    showToast(data)
                    navigate(-1)
                  }}
                  onSaveAndAdd={data => {
                    showToast(data)
                    changeOwnerDetails({})
                  }}
                  onSaveAndEdit={data => {
                    showToast(data)
                  }}
                />
              </div>
            </>
          )
        }}
      </Authorization>
    </FormLayout>
  )
}

export default VehicleOwnerRegistration
